import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../../api.js';
import type { Account } from '../../types/bookkeeping.js';

const FX_SCALE = 1_000_000_000n;

export interface JELineFormValue {
  account_id: string;
  currency: string;
  amount: string; // signed bigint string (minor units)
  fx_rate: string; // bigint × 1e9 as string
  description?: string;
}

export interface JournalEntryLineRowProps {
  index: number;
  value: JELineFormValue;
  accounts: Account[];
  entryDate: string;
  baseCurrency: string;
  canRemove: boolean;
  onChange: (v: JELineFormValue) => void;
  onRemove: () => void;
}

export function JournalEntryLineRow({
  value,
  accounts,
  entryDate,
  baseCurrency,
  canRemove,
  onChange,
  onRemove,
}: JournalEntryLineRowProps) {
  const sameCurrency = value.currency === baseCurrency || value.currency.length !== 3;

  const { data: fx } = useQuery({
    queryKey: ['fx', value.currency, baseCurrency, entryDate],
    queryFn: () =>
      api<{ rate: string; source: string }>(
        `/bookkeeping/fx-rates?date=${entryDate}&from=${value.currency}&to=${baseCurrency}`,
      ),
    enabled: !!value.currency && value.currency.length === 3 && !!entryDate && !sameCurrency,
    retry: false,
  });

  useEffect(() => {
    if (sameCurrency && value.fx_rate !== FX_SCALE.toString()) {
      onChange({ ...value, fx_rate: FX_SCALE.toString() });
    } else if (!sameCurrency && fx?.rate && value.fx_rate === '') {
      onChange({ ...value, fx_rate: fx.rate });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fx, value.currency, value, sameCurrency, onChange]);

  // When account is selected, auto-set currency to account's currency.
  // For same-currency accounts, set fx_rate = FX_SCALE immediately (no useEffect delay needed).
  // For foreign-currency accounts, clear fx_rate so useEffect fetches it from the API.
  function handleAccountChange(accountId: string) {
    const account = accounts.find((a) => a.id === accountId);
    if (account) {
      const isSame = account.currency === baseCurrency;
      onChange({
        ...value,
        account_id: accountId,
        currency: account.currency,
        fx_rate: isSame ? FX_SCALE.toString() : '',
      });
    } else {
      onChange({ ...value, account_id: accountId });
    }
  }

  let baseAmount = '0';
  try {
    if (value.amount && value.fx_rate) {
      baseAmount = ((BigInt(value.amount) * BigInt(value.fx_rate)) / FX_SCALE).toString();
    }
  } catch {
    baseAmount = '—';
  }

  return (
    <tr className="border-b">
      <td className="px-2 py-1">
        <select
          value={value.account_id}
          onChange={(e) => handleAccountChange(e.target.value)}
          className="w-full rounded border px-1 py-0.5 text-sm"
        >
          <option value="">—</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.code} {a.name} ({a.currency})
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-1">
        <input
          value={value.currency}
          maxLength={3}
          onChange={(e) =>
            onChange({ ...value, currency: e.target.value.toUpperCase(), fx_rate: '' })
          }
          className="w-16 rounded border px-1 py-0.5 text-sm uppercase"
        />
      </td>
      <td className="px-2 py-1">
        <input
          value={value.amount}
          onChange={(e) => onChange({ ...value, amount: e.target.value })}
          placeholder="+100 / -100"
          className="w-28 rounded border px-1 py-0.5 text-sm"
        />
      </td>
      <td className="px-2 py-1">
        <input
          value={value.fx_rate}
          onChange={(e) => onChange({ ...value, fx_rate: e.target.value })}
          disabled={sameCurrency}
          className="w-32 rounded border px-1 py-0.5 text-sm disabled:bg-slate-50 disabled:text-slate-400"
        />
      </td>
      <td className="px-2 py-1 text-right font-mono text-sm">{baseAmount}</td>
      <td className="px-2 py-1">
        <input
          value={value.description ?? ''}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          placeholder="Descripción"
          className="w-32 rounded border px-1 py-0.5 text-sm"
        />
      </td>
      <td className="px-2 py-1">
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          className="text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-30"
          title="Eliminar línea"
        >
          ×
        </button>
      </td>
    </tr>
  );
}
